import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { IconArrowLeft, IconCheckCircleOutline, IconClose } from '@wegic/icon';
import { Tooltip } from '@wegic/ui';
import '@wegic/ui/dist/components/Tooltip/index.css';
import classNames from 'classnames';
import { AnimatePresence, motion } from 'framer-motion';
import { useCallback, useRef, useState } from 'react';
import { collect } from '@/modules/Collect/helper';
import NotificationDetail from '../NotificationDetail';
import NotificationList from '../NotificationList';
import { SCALE_TRANSITION, scaleVariants, SLIDE_TRANSITION, slideVariants } from './const';
import styles from './index.module.less';
function ActionIconButton(param) {
    let { ariaLabel, disabled = false, tooltip, onClick, children } = param;
    const button = /*#__PURE__*/ _jsx("button", {
        type: "button",
        "aria-label": ariaLabel,
        className: classNames(styles['icon-button'], disabled && styles['icon-button-disabled']),
        onClick: onClick,
        disabled: disabled,
        children: children
    });
    if (!tooltip || disabled) {
        return button;
    }
    return /*#__PURE__*/ _jsx(Tooltip, {
        content: tooltip,
        position: "bottom",
        triggerProps: {
            popupAlign: {
                bottom: 8
            }
        },
        children: button
    });
}
function NotificationPanel(param) {
    let { notifications, loading, selectedId, selectedNotification, unreadCount, updateUnread, forYouUnread, activeTab, showBackButton = false, showCloseButton = true, onSwitchTab, onSelect, onGoBack, onMarkAllAsRead, onLoadMore, onClose, onAction } = param;
    const [direction, setDirection] = useState(1);
    const notificationsRef = useRef(notifications);
    notificationsRef.current = notifications;
    const activeTabRef = useRef(activeTab);
    activeTabRef.current = activeTab;
    const isDetail = Boolean(selectedId && selectedNotification);
    const title = intl.get('workspace.notifications.title');
    const backLabel = intl.get('workspace.notifications.action.back');
    const markAllLabel = intl.get('workspace.notifications.action.markAllAsRead');
    const handleSelect = useCallback((id)=>{
        setDirection(1);
        const item = notificationsRef.current.find((n)=>n.id === id);
        if (item) {
            collect('notification_detail_view', {
                notification_id: item.id,
                notification_title: item.title,
                category: activeTabRef.current,
                is_read: item.isRead
            });
        }
        onSelect(id);
    }, [
        onSelect
    ]);
    const handleGoBack = useCallback(()=>{
        setDirection(-1);
        onGoBack();
    }, [
        onGoBack
    ]);
    return /*#__PURE__*/ _jsxs("div", {
        className: styles.panel,
        children: [
            /*#__PURE__*/ _jsxs("div", {
                className: styles.header,
                children: [
                    /*#__PURE__*/ _jsx("div", {
                        className: styles['header-left'],
                        children: /*#__PURE__*/ _jsx(AnimatePresence, {
                            initial: false,
                            custom: direction,
                            mode: "popLayout",
                            children: isDetail || showBackButton ? /*#__PURE__*/ _jsxs(motion.button, {
                                type: "button",
                                className: styles['back-button'],
                                onClick: isDetail ? handleGoBack : onClose,
                                custom: direction,
                                variants: slideVariants,
                                initial: "enter",
                                animate: "center",
                                exit: "exit",
                                transition: SLIDE_TRANSITION,
                                children: [
                                    /*#__PURE__*/ _jsx(IconArrowLeft, {
                                        fontSize: 16
                                    }),
                                    /*#__PURE__*/ _jsx("span", {
                                        children: backLabel
                                    })
                                ]
                            }, "back") : /*#__PURE__*/ _jsx(motion.div, {
                                className: styles.title,
                                custom: direction,
                                variants: slideVariants,
                                initial: "enter",
                                animate: "center",
                                exit: "exit",
                                transition: SLIDE_TRANSITION,
                                children: title
                            }, "title")
                        })
                    }),
                    /*#__PURE__*/ _jsxs("div", {
                        className: styles['header-actions'],
                        children: [
                            /*#__PURE__*/ _jsx(AnimatePresence, {
                                initial: false,
                                children: !isDetail && unreadCount > 0 && /*#__PURE__*/ _jsx(motion.div, {
                                    variants: scaleVariants,
                                    initial: "hidden",
                                    animate: "visible",
                                    exit: "hidden",
                                    transition: SCALE_TRANSITION,
                                    children: /*#__PURE__*/ _jsx(ActionIconButton, {
                                        ariaLabel: markAllLabel,
                                        tooltip: markAllLabel,
                                        onClick: ()=>{
                                            collect('notification_mark_all_read', {
                                                unread_count: unreadCount
                                            });
                                            onMarkAllAsRead();
                                        },
                                        children: /*#__PURE__*/ _jsx(IconCheckCircleOutline, {
                                            fontSize: 16
                                        })
                                    })
                                }, "mark-all")
                            }),
                            showCloseButton && /*#__PURE__*/ _jsx(ActionIconButton, {
                                ariaLabel: "Close",
                                onClick: onClose,
                                children: /*#__PURE__*/ _jsx(IconClose, {
                                    fontSize: 16
                                })
                            })
                        ]
                    })
                ]
            }),
            /*#__PURE__*/ _jsx("div", {
                className: styles['body-wrapper'],
                children: /*#__PURE__*/ _jsx(AnimatePresence, {
                    initial: false,
                    custom: direction,
                    mode: "popLayout",
                    children: isDetail && selectedNotification ? /*#__PURE__*/ _jsx(motion.div, {
                        className: styles['body-content'],
                        custom: direction,
                        variants: slideVariants,
                        initial: "enter",
                        animate: "center",
                        exit: "exit",
                        transition: SLIDE_TRANSITION,
                        children: /*#__PURE__*/ _jsx(NotificationDetail, {
                            notification: selectedNotification,
                            activeTab: activeTab,
                            onAction: onAction
                        })
                    }, "detail") : /*#__PURE__*/ _jsx(motion.div, {
                        className: styles['body-content'],
                        custom: direction,
                        variants: slideVariants,
                        initial: "enter",
                        animate: "center",
                        exit: "exit",
                        transition: SLIDE_TRANSITION,
                        children: /*#__PURE__*/ _jsx(NotificationList, {
                            notifications: notifications,
                            loading: loading,
                            activeTab: activeTab,
                            updateUnread: updateUnread,
                            forYouUnread: forYouUnread,
                            onSwitchTab: onSwitchTab,
                            onSelect: handleSelect,
                            onLoadMore: onLoadMore
                        })
                    }, "list")
                })
            })
        ]
    });
}
export default NotificationPanel;
